import { createActionCreator } from '@pressreader/appstore';
import { IIssueInfo } from '@pressreader/content-types';
import { IPoint } from '@pressreader/geometry';
import { IContextMenuPosition } from '@pressreader/popups-types';
import { IFont } from '@pressreader/types';

import { MODULE_NAME } from '../constants';
import { TranslateMenuContext } from '../types';
import { LineSpacing } from '../types/line.spacing';
import { TopBarNavItemParams } from '../types/topbar.menu';

const pre = `${MODULE_NAME}/`;

interface IChangeInterfaceLanguagePayload {
    languageCode: string;
}

interface IAvailableFontsChangedPayload {
    fonts: IFont[];
}

interface IChangeFontPayload {
    fontId: string;
}

interface IChangeFontDonePayload {
    fontId: string;
}

interface IChangeFontScalePayload {
    fontScale: number;
}

interface IFontScaleChangedPayload {
    fontScale: number;
}

interface ChangeLineSpacingPayload {
    lineSpacing: LineSpacing;
}

interface LineSpacingChangedPayload {
    lineSpacing: LineSpacing;
}

interface IOpenChangeInterfaceLanguageMenuPayload {
    center: IPoint;

    parentMenuId?: string;
}

interface IOpenFontsMenuPayload {
    showFonts?: boolean;

    center?: IPoint;

    parentMenuId?: string;
}

interface IOpenFontTypeMenuPayload {
    parentMenuId: string;

    /** Position of the parent fonts menu, reused so the submenu opens in place of it. */
    position?: IContextMenuPosition;
}

interface IOpenLegacyOptionsMenu {
    center: IPoint;
}

interface OpenTranslateMenuPopupPayload extends TranslateMenuContext {
    center?: IPoint;
    parentMenuId?: string;
    alignTop?: boolean;
}

interface TranslateMenuLanguageCodePayload {
    languageCode: string | null;
}

export const changeInterfaceLanguageAction = createActionCreator<IChangeInterfaceLanguagePayload>(`${pre}change_interface_language`);

export const changeFontAction = createActionCreator<IChangeFontPayload>(`${pre}change_font`);
export const changeFontDone = createActionCreator<IChangeFontDonePayload>(`${pre}change_font_done`);

export const availableFontsChanged = createActionCreator<IAvailableFontsChangedPayload>(`${pre}available_fonts_changed`);
export const changeFontScaleAction = createActionCreator<IChangeFontScalePayload>(`${pre}change_font_scale`);
export const increaseFontScaleAction = createActionCreator(`${pre}increase_font_scale`);
export const decreaseFontScaleAction = createActionCreator(`${pre}decrease_font_scale`);
export const fontScaleChanged = createActionCreator<IFontScaleChangedPayload>(`${pre}font_scale_changed`);

export const changeLineSpacingAction = createActionCreator<ChangeLineSpacingPayload>(`${pre}change_line_spacing`);
export const lineSpacingChanged = createActionCreator<LineSpacingChangedPayload>(`${pre}line_spacing_changed`);

export const openChangeInterfaceLanguageMenuAction = createActionCreator<IOpenChangeInterfaceLanguageMenuPayload>(
    `${pre}open_change_interface_language_menu`,
);
export const openFontsMenuAction = createActionCreator<IOpenFontsMenuPayload>(`${pre}open_fonts_menu`);
export const openFontTypeMenuAction = createActionCreator<IOpenFontTypeMenuPayload>(`${pre}open_font_type_menu`);

export const openLegacyOptionsMenuAction = createActionCreator<IOpenLegacyOptionsMenu>(`${pre}open_legacy_options_menu`);

export const openTranslateMenuAction = createActionCreator<OpenTranslateMenuPopupPayload>(`${pre}open_translate_menu`);
export const translateMenuOpened = createActionCreator(`${pre}translate_menu_opened`);
export const translateMenuClosed = createActionCreator(`${pre}translate_menu_closed`);
export const translateMenuLanguageCodeChanged = createActionCreator<TranslateMenuLanguageCodePayload>(`${pre}translate_menu_language_code_changed`);

export const legacyAppLoadBegan = createActionCreator(`${pre}legacy_app_load_began`);
export const legacyAppLoadDone = createActionCreator(`${pre}legacy_app_load_done`);
export const legacyAppLoadFailed = createActionCreator(`${pre}legacy_app_load_failed`);

export const checkIssueReadLimitsAction = createActionCreator<IIssueInfo['restrictions']['rateLimitsInfo'] | undefined>(
    `${pre}check_issue_read_limits`,
);

/**
 * Stops a running history scroll restore. Dispatch before a programmatic scroll-to-top that
 * happens outside navigation (e.g. a content reload replacing the page in place), so the
 * restore does not drag the replaced content back to the stale saved position.
 */
export const abortScrollRestoreAction = createActionCreator(`${pre}abort_scroll_restore`);

// Top bar menu item actions
export const addTopBarMenuItemsAction = createActionCreator<{
    items: TopBarNavItemParams[];
}>(`${pre}add_top_bar_menu_items-action`);
export const removeTopBarMenuItemsAction = createActionCreator<{
    itemUniqKeys: TopBarNavItemParams['uniqKey'][];
}>(`${pre}remove_top_bar_menu_items-action`);
export { IAvailableFontsChangedPayload, IChangeFontDonePayload };
